import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ss_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith("/login")) {
      localStorage.removeItem("ss_token");
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Human-readable message from any axios error (handles blob responses, timeouts, gateway errors)
export async function describeError(err) {
  const res = err?.response;
  if (!res) return err?.code === "ECONNABORTED" ? "The request took too long. Try fewer photos at a time." : "Network error — check your connection and try again.";
  let detail = res.data?.detail;
  if (res.data instanceof Blob) {
    try { detail = JSON.parse(await res.data.text())?.detail; } catch { detail = null; }
  }
  if (detail) return formatApiError(detail);
  if (res.status === 413) return detail ? formatApiError(detail) : "That selection is too large to send at once. Try fewer photos.";
  if (res.status === 502 || res.status === 504) return "The server took too long to respond. Try fewer photos at a time.";
  if (res.status === 401) return "Your session expired — please sign in again.";
  return `Something went wrong (HTTP ${res.status}). Please try again.`;
}

// token-aware file URL for <img> tags (query-param auth)
export function fileUrl(fileId) {
  if (!fileId) return null;
  const token = localStorage.getItem("ss_token");
  return `${API}/files/${fileId}?auth=${token}`;
}

// Resolve a displayable avatar URL for a user/record (uploaded file takes priority).
export function avatarUrl(obj) {
  if (!obj) return null;
  if (obj.picture_file_id) return fileUrl(obj.picture_file_id);
  return obj.picture || null;
}

// Fetch a (possibly auth-protected) file and trigger a browser download / save.
export async function downloadFile(src, name = "download") {
  try {
    const res = await fetch(src);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch {
    window.open(src, "_blank");
  }
}

export default api;
